/**
 * Query Parameter Utilities
 * 
 * Central utilities for managing URL query parameters consistently across the app.
 * 
 * @module lib/queryParams
 */

/**
 * Standard query parameter keys used throughout the application
 */
export const QueryParams = {
  /** Booking ID for confirmation and detail pages */
  BOOKING_ID: 'booking-id',
  
  /** Tenant handle (e.g., 'drivingrangecollective') */
  TENANT_HANDLE: 'tenant-handle',
  
  /** Club ID */
  CLUB_ID: 'club-id',
  
  /** Payment provider (e.g., 'square', 'stripe') */
  PAYMENT_PROVIDER: 'payment-provider',

  /** User ID for account setup and other user-specific flows */
  USER_ID: 'user-id',

  /** Token for authentication flows (setup, password reset, etc.) */
  TOKEN: 'token',

  /** Pre-selected subscription plan for deep-link sharing (e.g., 'membership-5', 'pass-12') */
  PLAN: 'plan',

  /**
   * Selected lesson package id (BUT-841) — the durable path that survives a
   * remount (e.g. a guest signing in mid-purchase). Deliberately NOT in
   * PRESERVED_QUERY_PARAMS, same reasoning as PROMO below: it must not ride
   * along into every later navigation.
   */
  LESSON: 'lesson',

  /**
   * Promo code from a tenant-distributed link (D21/D22, slice 7a) — on
   * `/subscriptions` or the booking flow (`/`). Captured into sessionStorage by
   * `usePromoCodeCapture`, one key per funnel; deliberately NOT in
   * PRESERVED_QUERY_PARAMS — carrying it on every in-app navigation would leak
   * the code into the confirmation URL and beyond.
   */
  PROMO: 'promo',

  /**
   * `1` on a club link's landing (deep-links D12): opens the app-download sheet
   * once, on phones, then is scrubbed. NOT in PRESERVED_QUERY_PARAMS — it must
   * never ride along into later navigations.
   */
  GET_APP: 'get-app',
} as const;

/**
 * Build a query string from an object of parameters
 * 
 * @param params - Object with query parameter key-value pairs
 * @returns Query string (without leading '?')
 * 
 * @example
 * ```typescript
 * buildQueryString({
 *   [QueryParams.BOOKING_ID]: '184',
 *   [QueryParams.TENANT_HANDLE]: 'drivingrangecollective',
 * })
 * // => 'booking-id=184&tenant-handle=drivingrangecollective'
 * ```
 */
export function buildQueryString(params: Record<string, string | number | undefined | null>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
}

/**
 * Query parameters that should be automatically preserved across navigation
 * 
 * These params will be automatically added to URLs by `TenantLink` and
 * `useTenantNavigate` (unless explicitly disabled), and `useTenantSearchParams`
 * refuses to write them — they change only through a full-page tenant switch.
 */
export const PRESERVED_QUERY_PARAMS = [
  QueryParams.TENANT_HANDLE,
  QueryParams.CLUB_ID,
] as const;

/**
 * Build a full URL with query parameters
 * 
 * @param path - URL path
 * @param params - Object with query parameter key-value pairs
 * @param currentParams - Optional URLSearchParams to merge with (for preserving existing params)
 * @returns Full URL with query string
 * 
 * Prefer `TenantLink` / `useTenantNavigate` from `~/lib/navigation`, which call
 * this for you. Reach for it directly only where no hook can run (a
 * `<Navigate to>`), or to pass an explicit tenant/club that should override the
 * current URL's (explicit params win over preserved ones).
 * 
 * @example
 * ```typescript
 * // Basic usage
 * buildUrl('/confirmed', {
 *   [QueryParams.BOOKING_ID]: '184',
 *   [QueryParams.TENANT_HANDLE]: 'drivingrangecollective',
 * })
 * // => '/confirmed?booking-id=184&tenant-handle=drivingrangecollective'
 * 
 * // With preserved params
 * const [searchParams] = useTenantSearchParams();
 * buildUrl('/confirmed', { [QueryParams.BOOKING_ID]: '184' }, searchParams)
 * // Automatically preserves tenant-handle and club-id from searchParams
 * ```
 */
export function buildUrl(
  path: string,
  params: Record<string, string | number | undefined | null>,
  currentParams?: URLSearchParams
): string {
  // Start with new params
  const mergedParams = { ...params };
  
  // Merge in preserved params from current URL if available
  if (currentParams) {
    PRESERVED_QUERY_PARAMS.forEach(key => {
      // Only add if not already specified in params
      if (mergedParams[key] === undefined) {
        const value = currentParams.get(key);
        if (value !== null) {
          mergedParams[key] = value;
        }
      }
    });
  }
  
  const queryString = buildQueryString(mergedParams);
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * Build an in-app entry URL that lands on a specific club within a tenant.
 *
 * For **full-page** (cross-tenant) navigation only — the SPA param-preserving
 * helpers (`TenantLink` / `useTenantNavigate`) are the wrong tool here: they keep
 * the *current* tenant/club, whereas a tenant switch must set a *fresh* tenant +
 * club and reload so the app re-initializes its tenant context. Returns a
 * path+query string for an `<a href>` or `window.location.href`.
 *
 * `club-id` is included when known — the app needs it to resolve a club on
 * landing; omit it only when no club is resolvable (caller falls back to a
 * tenant-only URL).
 */
export function buildClubEntryUrl(opts: {
  tenantHandle: string;
  clubId?: number | string | null;
}): string {
  const params = new URLSearchParams();
  params.set(QueryParams.TENANT_HANDLE, opts.tenantHandle);
  if (opts.clubId !== undefined && opts.clubId !== null && opts.clubId !== '') {
    params.set(QueryParams.CLUB_ID, String(opts.clubId));
  }
  return `/?${params.toString()}`;
}
